import React, { Fragment, useRef } from "react";
import { Formik } from "formik";
import {
  updateMerchantAccountPrices,
  archiveMerchantAccountPrices
} from "actions/price";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import StripeExternalLink from "components/StripeExternalLink";
import { getStripeDestination } from "utils/stripe";
import {
  getUpdatePriceParams,
  prepareInitialValuesFromPrice
} from "utils/price";
import PriceFormSection from "./PriceFormSection";
import FormActions from "components/FormActions";
import { UPDATE_PRICE_SCHEMA } from "constants/validation";
import { withRouter } from "next/router";
import { getPriceArchiveModalSlides } from "utils/constants/modal";
import ConfirmIconButton from "components/ConfirmIconButton";
import { useCtxReferences } from "utils/selectors";
import { getAccountConnectStatusProps } from "utils/merchantAccount";
import Padder from "components/Padder";
import merge from "lodash/merge";
import isEmpty from "lodash/isEmpty";

const DEFAULT_CLASSES = {
  header: {
    container: "flex flex-column w-100 sticky bg-white top-0 z-6 ph3",
    titleWrapper: "flex flex-row justify-between items-center",
    title: "f4 fw5"
  },
  actions: {
    container: "flex flex-row items-center pv3 bb hairline-1",
    navigation: "flex w-100 items-center",
    titleWrapper: "flex flex-row items-center",
    title: "fw6 f3 word-break",
    form: "flex flex-shrink-0"
  },
  content: {
    container: "bg-white h-100"
  }
};

const ProductPriceForm = ({ name, product, price, actions, customClasses }) => {
  const formRef = useRef(null);
  const { resource, resourceUUID, merchantAccount } = useCtxReferences();

  const classes = merge(DEFAULT_CLASSES, customClasses);

  const account =
    merchantAccount && getAccountConnectStatusProps(merchantAccount);

  const updatePrice = (formValues, formikActions) => {
    if (merchantAccount && merchantAccount.uuid) {
      const updatedPrice = getUpdatePriceParams(
        formRef.current.initialValues,
        formValues
      );

      if (!isEmpty(updatedPrice)) {
        actions.updateMerchantAccountPrices(
          {
            resourceUUID,
            resource,
            uuid: merchantAccount.uuid,
            data: {
              prices: [updatedPrice]
            }
          },
          (err) => {
            if (err) {
              formikActions.setErrors(err);
            } else {
              formikActions.resetForm({ values: formValues });
            }
            formikActions.setSubmitting(false);
          }
        );
      } else {
        console.error("No price updates to send");
        formikActions.resetForm({ values: formValues });
        formikActions.setSubmitting(false);
      }
    } else {
      console.error("No merchant account uuid for price update");
    }
  };

  const archivePrice = (callback) => {
    const deleteDataInput = {
      prices: [
        {
          id: price.id,
          product: price.product
        }
      ]
    };

    actions.archiveMerchantAccountPrices(
      {
        resourceUUID,
        resource,
        uuid: merchantAccount.uuid,
        data: deleteDataInput
      },
      (err, prices) => {
        callback(err, prices);
      }
    );
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={prepareInitialValuesFromPrice(price)}
      onSubmit={updatePrice}
      validate={async (values) =>
        UPDATE_PRICE_SCHEMA.validate(values)
          .then(() => ({}))
          .catch((err) => ({
            [err.path]: err.message
          }))
      }
    >
      {({ values }) => {
        return (
          <Fragment>
            <div className={classes.header.container}>
              <div className={classes.actions.container}>
                <div className={classes.actions.navigation}>
                  <div className={classes.actions.titleWrapper}>
                    <div className={classes.actions.title}>
                      {values.formattedPrice}
                    </div>
                  </div>
                </div>
                <div className={classes.actions.form}>
                  <FormActions theme="slim">
                    <div className="pr2">
                      <ConfirmIconButton
                        tooltip="Archive"
                        slides={getPriceArchiveModalSlides({ product, price })}
                        icon="archive"
                        onConfirm={archivePrice}
                      >
                        <div className="fl pv3 relative z-1">
                          <StripeExternalLink
                            account={account}
                            copy={`Manage on Stripe`}
                            href={getStripeDestination({
                              path: `prices/${price.id}`
                            })}
                          />
                        </div>
                      </ConfirmIconButton>
                    </div>
                  </FormActions>
                </div>
              </div>
            </div>
            <div className="ph3 pt3">
              <PriceFormSection name={name} product={product} />
            </div>
            <Padder />
          </Fragment>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      updateMerchantAccountPrices,
      archiveMerchantAccountPrices
    },
    dispatch
  )
});

export default withRouter(connect(null, mapDispatchToProps)(ProductPriceForm));
